import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Pagination, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SupplierAdvanceService } from '../../services/supplierAdvance.service';
import { SupplierService } from '../../services/supplier.service';
import { SupplierBillService } from '../../services/supplierBill.service';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { loadSuppliersSafely } from '../../utils/supplierAccess';
import { SupplierAdvance, SupplierAdvanceAgeing } from '../../interfaces/procurement/supplierAdvance.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { PurchaseOrder } from '../../interfaces/procurement/purchaseOrder.interface';
import { Account } from '../../interfaces/accounting.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTable from '../common/ScrollableTable';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import AllocateDialog, { AllocationTargetOption } from '../allocation/AllocateDialog';
import { depositAccountOptions } from '../../utils/postingAccounts';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

/** An advance only makes sense against a PO that is still to be supplied: approved, ordered or part-received. */
export const OPEN_PO_STATUSES: PurchaseOrder['status'][] = ['APPROVED', 'ORDERED', 'PARTIALLY_RECEIVED'];

export const SupplierAdvanceList: React.FC = () => {
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_MANAGE);

  const [rows, setRows] = useState<SupplierAdvance[]>([]);
  const [ageing, setAgeing] = useState<SupplierAdvanceAgeing | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });
  const [busyId, setBusyId] = useState<string | null>(null);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [poId, setPoId] = useState('');
  const [advanceDate, setAdvanceDate] = useState(toLocalIsoDate());
  const [amount, setAmount] = useState<number | ''>('');
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // allocate dialog
  const [allocFor, setAllocFor] = useState<SupplierAdvance | null>(null);
  const [targets, setTargets] = useState<AllocationTargetOption[]>([]);

  const load = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const r = await SupplierAdvanceService.listByInstitution(page, 10);
      setRows(r.content);
      setPageInfo({ currentPage: r.number, totalPages: r.totalPages, totalElements: r.totalElements });
      setAgeing(await SupplierAdvanceService.ageing());
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier advances');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(0); }, [load]);

  const openCreate = async () => {
    setSupplierId(''); setPoId(''); setAmount(''); setPaymentAccountId('');
    setPaymentMethod(''); setReference(''); setNotes(''); setAdvanceDate(toLocalIsoDate());
    setCreateOpen(true);
    const r = await loadSuppliersSafely(() => SupplierService.listByInstitution(0, 500, 'ALL'));
    setSuppliers(r.suppliers);
    if (r.error) toast.showError(r.error);
    AccountService.getAccountsByType('ASSET')
      .then((a: Account[]) => setAccounts(depositAccountOptions(a)))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  };

  useEffect(() => {
    setPoId('');
    if (!createOpen || !supplierId) { setOrders([]); return; }
    Promise.resolve(PurchaseOrderService.listByInstitution(0, 500))
      .then(r => setOrders((r.content ?? []).filter(o => o.supplierId === supplierId && OPEN_PO_STATUSES.includes(o.status))))
      .catch(() => setOrders([]));
  }, [supplierId, createOpen]);

  const createInvalid = !supplierId || amount === '' || Number(amount) <= 0 || !paymentAccountId || !advanceDate;

  const handleCreate = async () => {
    if (createInvalid) return;
    setSaving(true);
    try {
      const adv = await SupplierAdvanceService.create({
        institutionId: AuthService.getSelectedInstitution()!!,
        supplierId,
        purchaseOrderId: poId || undefined,
        advanceDate,
        amount: Number(amount),
        paymentAccountId,
        paymentMethod: paymentMethod.trim() || undefined,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.showSuccess(`Advance ${adv.advanceNumber} recorded`);
      if (adv.withholdingWarning) {
        toast.showError('This supplier has a default VDS/TDS rule. No withholding is taken on an advance; check withholding when the bill is paid.');
      }
      setCreateOpen(false);
      load(0);
    } catch (e) {
      toast.showApiError(e, 'Failed to record advance');
    } finally {
      setSaving(false);
    }
  };

  const openAllocate = async (a: SupplierAdvance) => {
    try {
      const r = await SupplierBillService.listBySupplier(a.supplierId, 0, 500);
      setTargets((r.content ?? [])
        .filter(b => b.status === 'UNPAID' || b.status === 'PARTIALLY_PAID')
        .map(b => ({ id: b.id, label: b.billNumber, outstanding: b.totalAmount - b.paidAmount - (b.debitedAmount ?? 0) }))
        .filter(t => t.outstanding > 0.004));
      setAllocFor(a);
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier bills');
    }
  };

  const reverse = async (a: SupplierAdvance) => {
    setBusyId(a.id);
    try {
      await SupplierAdvanceService.reverse(a.id);
      toast.showSuccess('Advance reversed');
      load(pageInfo.currentPage);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse advance');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.advances.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Supplier Advances</Typography>
        {canManage && <Button data-help="procurement.advances.new" variant="contained" onClick={openCreate}>Record Advance</Button>}
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          {ageing && (
            <Paper sx={{ p: 2, mb: 3 }} data-help-section="procurement.advances.ageing">
              <Typography variant="h6" sx={{ mb: 1 }}>Unapplied advance ageing (as of {formatDate(ageing.asOf)})</Typography>
              <ScrollableTable>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell data-help="procurement.advances.ageing.col.supplier">Supplier</TableCell>
                      <TableCell data-help="procurement.advances.ageing.col.b1" align="right">0-30</TableCell>
                      <TableCell data-help="procurement.advances.ageing.col.b2" align="right">31-60</TableCell>
                      <TableCell data-help="procurement.advances.ageing.col.b3" align="right">61-90</TableCell>
                      <TableCell data-help="procurement.advances.ageing.col.b4" align="right">&gt;90</TableCell>
                      <TableCell data-help="procurement.advances.ageing.col.total" align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ageing.rows.map(r => (
                      <TableRow key={r.supplierId}>
                        <TableCell>{r.supplierName ?? r.supplierId}</TableCell>
                        <TableCell align="right">{formatMoney(r.bucket0To30)}</TableCell>
                        <TableCell align="right">{formatMoney(r.bucket31To60)}</TableCell>
                        <TableCell align="right">{formatMoney(r.bucket61To90)}</TableCell>
                        <TableCell align="right">{formatMoney(r.bucket90Plus)}</TableCell>
                        <TableCell align="right">{formatMoney(r.total)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell><b>Total</b></TableCell>
                      <TableCell align="right"><b>{formatMoney(ageing.bucket0To30)}</b></TableCell>
                      <TableCell align="right"><b>{formatMoney(ageing.bucket31To60)}</b></TableCell>
                      <TableCell align="right"><b>{formatMoney(ageing.bucket61To90)}</b></TableCell>
                      <TableCell align="right"><b>{formatMoney(ageing.bucket90Plus)}</b></TableCell>
                      <TableCell align="right"><b>{formatMoney(ageing.total)}</b></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </ScrollableTable>
            </Paper>
          )}

          <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>
          <Paper data-help-section="procurement.advances.table">
            <ScrollableTable>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell data-help="procurement.advances.col.number">Advance</TableCell>
                    <TableCell data-help="procurement.advances.col.supplier">Supplier</TableCell>
                    <TableCell data-help="procurement.advances.col.po">PO</TableCell>
                    <TableCell data-help="procurement.advances.col.date">Date</TableCell>
                    <TableCell data-help="procurement.advances.col.amount" align="right">Amount</TableCell>
                    <TableCell data-help="procurement.advances.col.unapplied" align="right">Unapplied</TableCell>
                    <TableCell data-help="procurement.advances.col.status">Status</TableCell>
                    <TableCell data-help="procurement.advances.col.actions" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(a => (
                    <TableRow key={a.id}>
                      <TableCell>{a.advanceNumber}</TableCell>
                      <TableCell>{a.supplierName ?? '—'}</TableCell>
                      <TableCell>{a.purchaseOrderNumber ?? '—'}</TableCell>
                      <TableCell>{formatDate(a.advanceDate)}</TableCell>
                      <TableCell align="right">{formatMoney(a.amount)}</TableCell>
                      <TableCell align="right">{formatMoney(a.unappliedAmount)}</TableCell>
                      <TableCell>
                        <Chip data-help="procurement.advances.status" size="small" label={humanizeEnum(a.status)} color={a.status === 'POSTED' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell align="right">
                        {canManage && a.status === 'POSTED' && a.unappliedAmount > 0.004 && (
                          <Button data-help="procurement.advances.apply" size="small" onClick={() => openAllocate(a)}>Apply to bill</Button>
                        )}
                        {canManage && a.status === 'POSTED' && (
                          <Button data-help="procurement.advances.reverse" size="small" color="error" disabled={busyId === a.id} onClick={() => reverse(a)}>Reverse</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={8}>No supplier advances yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollableTable>
          </Paper>
          {pageInfo.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={pageInfo.totalPages} page={pageInfo.currentPage + 1} color="primary"
                onChange={(_e, p) => load(p - 1)} />
            </Box>
          )}
        </>
      )}

      <Dialog data-help-page="procurement.advanceForm.page" open={createOpen} onClose={() => !saving && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record supplier advance</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              No VDS/TDS is withheld on an advance. If the supplier is subject to withholding, check it when the
              bill is paid; applying an advance to a bill does not withhold anything.
            </Alert>
            <SearchableSelect<Supplier>
              data-help="procurement.advanceForm.field.supplier"
              label="Supplier" required options={suppliers} value={supplierId} onChange={setSupplierId}
              getOptionLabel={s => s.name}
            />
            <SearchableSelect<PurchaseOrder>
              data-help="procurement.advanceForm.field.po"
              label="Purchase order (optional)" options={orders} value={poId} onChange={setPoId}
              getOptionLabel={o => `${o.poNumber} - ${humanizeEnum(o.status)} - ${formatMoney(o.totalAmount)}`} noOptionsText="No open or approved purchase orders for this supplier"
            />
            <TextField data-help="procurement.advanceForm.field.date" label="Date" type="date" required fullWidth value={advanceDate}
              onChange={e => setAdvanceDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField data-help="procurement.advanceForm.field.amount" label="Amount" type="number" required fullWidth value={amount}
              onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ min: 0, step: '0.01' }} />
            <AccountAutocomplete data-help="procurement.advanceForm.field.account" accounts={accounts} value={paymentAccountId} onChange={setPaymentAccountId}
              label="Paid from account" required />
            <TextField data-help="procurement.advanceForm.field.method" label="Method" fullWidth value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
              inputProps={{ maxLength: 40 }} />
            <TextField data-help="procurement.advanceForm.field.reference" label="Reference" fullWidth value={reference} onChange={e => setReference(e.target.value)}
              inputProps={{ maxLength: 255 }} />
            <TextField data-help="procurement.advanceForm.field.notes" label="Notes" fullWidth multiline minRows={2} value={notes} onChange={e => setNotes(e.target.value)}
              inputProps={{ maxLength: 1000 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.advanceForm.cancel" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
          <Button data-help="procurement.advanceForm.save" variant="contained" onClick={handleCreate} disabled={saving || createInvalid}>
            {saving ? 'Saving…' : 'Record advance'}
          </Button>
        </DialogActions>
      </Dialog>

      {allocFor && (
        <AllocateDialog
          open
          onClose={() => setAllocFor(null)}
          onAllocated={() => load(pageInfo.currentPage)}
          creditType="SUPPLIER_ADVANCE"
          creditId={allocFor.id}
          available={allocFor.unappliedAmount}
          targetType="SUPPLIER_BILL"
          targets={targets}
          title={`Apply ${allocFor.advanceNumber} to bill`}
          targetLabel="Open bill"
        />
      )}
    </Box>
  );
};

export default SupplierAdvanceList;
